import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, Circle, X, PartyPopper, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'account_created', label: 'Create your account', action: null },
  { key: 'crm_connected', label: 'Connect your CRM', action: 'crm' },
  { key: 'openai_key_added', label: 'Add your OpenAI API key', action: 'settings' },
  { key: 'bot_configured', label: 'Choose your bot type and set your prompt', action: 'bot' },
  { key: 'bot_active', label: 'Activate your bot', action: 'bot' },
];

export default function OnboardingChecklist({ onScrollTo }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/onboarding/status`, { withCredentials: true });
      setStatus(data);
      if (data.dismissed) setDismissed(true);
    } catch { /* ignore */ }
  };

  const handleDismiss = async () => {
    setDismissed(true);
    try {
      await axios.post(`${API}/api/onboarding/dismiss`, {}, { withCredentials: true });
    } catch { /* ignore */ }
  };

  const handleAction = (action) => {
    if (action === 'settings') {
      navigate('/settings');
    } else if (action === 'crm' || action === 'bot') {
      onScrollTo?.(action);
    }
  };

  // Don't render if dismissed or still loading
  if (dismissed || !status) return null;
  // Don't render if all complete AND previously dismissed
  if (status.all_complete && status.dismissed) return null;

  const completedCount = STEPS.filter((s) => status[s.key]).length;
  const allComplete = status.all_complete;

  return (
    <div
      className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-5 mb-5 transition-all duration-300"
      data-testid="onboarding-checklist"
    >
      {allComplete ? (
        /* All complete state */
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PartyPopper className="w-5 h-5 text-[#7B2FFF]" />
            <div>
              <p className="text-sm font-semibold text-[#EDEDF0]">
                You're all set! Your bot is live.
              </p>
              <p className="text-xs text-[#71717A] mt-0.5">All onboarding steps completed</p>
            </div>
          </div>
          <Button
            data-testid="onboarding-dismiss-button"
            onClick={handleDismiss}
            variant="ghost"
            size="sm"
            className="text-[#71717A] hover:text-[#EDEDF0] hover:bg-[#ffffff08] h-8 px-3"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        /* In-progress state */
        <>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3
                className="text-sm font-semibold text-[#EDEDF0]"
                style={{ fontFamily: "'Unbounded', sans-serif" }}
              >
                Get started with HighConnect
              </h3>
              <p className="text-xs text-[#71717A] mt-0.5">
                {completedCount} of {STEPS.length} steps completed
              </p>
            </div>
            <Button
              data-testid="onboarding-dismiss-button"
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="text-[#71717A] hover:text-[#EDEDF0] hover:bg-[#ffffff08] h-8 px-2"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-[#27272A] rounded-full mb-4 overflow-hidden">
            <div
              className="h-full bg-[#7B2FFF] rounded-full transition-all duration-500"
              style={{ width: `${(completedCount / STEPS.length) * 100}%` }}
            />
          </div>

          {/* Steps */}
          <div className="space-y-1">
            {STEPS.map(({ key, label, action }) => {
              const done = status[key];
              return (
                <button
                  key={key}
                  data-testid={`onboarding-step-${key}`}
                  onClick={() => !done && action && handleAction(action)}
                  disabled={done || !action}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-all duration-150 group ${
                    done
                      ? 'opacity-60'
                      : action
                        ? 'hover:bg-[#ffffff06] cursor-pointer'
                        : ''
                  }`}
                >
                  {done ? (
                    <div className="w-5 h-5 rounded-full bg-[#7B2FFF]/15 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-[#7B2FFF]" />
                    </div>
                  ) : (
                    <Circle className="w-5 h-5 text-[#27272A] shrink-0" />
                  )}
                  <span
                    className={`text-sm flex-1 ${
                      done ? 'text-[#71717A] line-through' : 'text-[#EDEDF0]'
                    }`}
                  >
                    {label}
                  </span>
                  {!done && action && (
                    <ChevronRight className="w-4 h-4 text-[#71717A] opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
